import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { decode, encode } from 'base-64';
import { FiSearch, FiX } from 'react-icons/fi';
import Header from '../../component/loaderComponent/Header/Header';
import NoDataFound from '../../component/noDataComponent/NoDataFound';
import { notify } from '../../services/notification';
import { Image_Url } from '../../constants/imageUrl/ImageUrl';
import './customerDetailPage.scss';
import { getCustomerDetailListAction, downloadCustomerDetailExcelAction, setCustomerDetailStateAction, clearCustomerDetailStateAction } from '../../actions/targetAchievementAction';
import { formatMonth, isCurrentMonth } from '../../constants/dateTimeUtils/dateTimeUtils';
import { ITEMS_PER_PAGE } from '../../constants/constant';

export default function CustomerDetailPage() {
  const { apsCode: encodedApsCode, month: encodedMonth } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);

  // Decode URL parameters
  const apsCode = decode(encodedApsCode);
  const month = decode(encodedMonth);

  // Get persisted state from Redux
  const reduxState = useSelector(state => state.targetAchievement.customerDetailPage);

  // Check if Redux state matches current page
  const stateMatchesCurrentPage = reduxState.apsCode === apsCode && reduxState.month === month;

  // State - Initialize from Redux if available and matches current page
  const [dataList, setDataList] = useState(stateMatchesCurrentPage ? reduxState.dataList : []);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(stateMatchesCurrentPage ? reduxState.currentPage : 1);
  const [totalCount, setTotalCount] = useState(stateMatchesCurrentPage ? reduxState.totalCount : 0);
  const [searchField, setSearchField] = useState(stateMatchesCurrentPage ? reduxState.searchField : '');
  const [searchValue, setSearchValue] = useState(stateMatchesCurrentPage ? reduxState.searchValue : '');

  const searchFieldOptions = [
    { value: "customer_code", label: "Customer Code" },
    { value: "customer_name", label: "Customer Name" },
  ];

  // Pagination calculations
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE)),
    [totalCount]
  );

  const pageWindow = useMemo(() => {
    const pages = [];
    const maxButtons = 7;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const showLeftEllipsis = currentPage > 4;
    const showRightEllipsis = currentPage < totalPages - 3;

    pages.push(1);
    if (showLeftEllipsis) pages.push("left-ellipsis");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) pages.push(i);

    if (showRightEllipsis) pages.push("right-ellipsis");
    pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  // Initial data fetch when component mounts or URL params change
  useEffect(() => {
    // Only fetch if we don't have matching Redux state
    if (!stateMatchesCurrentPage) {
      setCurrentPage(1);
      setSearchField('');
      setSearchValue('');
      fetchData(1, '', '');
    }
    isInitialMount.current = false;
  }, [apsCode, month]);

  // Fetch data when page changes (but skip on initial mount to avoid duplicate fetch)
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData(currentPage, searchField, searchValue);
    }
  }, [currentPage]);

  const fetchData = async (page = currentPage, field = searchField, value = searchValue) => {
    try {
      setLoading(true);

      let queryParam = "";
      if (field && value.trim()) {
        queryParam = `${field}=${value.trim()}`;
      }

      const response = await dispatch(
        getCustomerDetailListAction(apsCode, month, page, ITEMS_PER_PAGE, queryParam)
      );

      if (response?.status === 200) {
        const apiData = response.data.results?.data || [];
        const dataToSet = Array.isArray(apiData) ? apiData : [];
        const count = response.data.count || 0;

        setDataList(dataToSet);
        setTotalCount(count);

        // Save to Redux for state persistence
        dispatch(setCustomerDetailStateAction(
          apsCode,
          month,
          field,
          value,
          page,
          dataToSet,
          count
        ));

        // Adjust page if beyond last page
        const lastPage = Math.max(1, Math.ceil(count / ITEMS_PER_PAGE));
        if (page > lastPage) setCurrentPage(lastPage);
      }
    } catch (error) {
      notify.error("Failed to load customer details");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchField || !searchValue.trim()) return;

    const trimmed = searchValue.trim();

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData(1, searchField, trimmed);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchField && searchValue.trim()) {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchField('');
    setSearchValue('');

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData(1, '', '');
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);

      let queryParam = "";
      if (searchField && searchValue.trim()) {
        queryParam = `${searchField}=${searchValue.trim()}`;
      }

      const blob = await dispatch(
        downloadCustomerDetailExcelAction(apsCode, month, queryParam)
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customer_details_${apsCode}_${month}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      notify.success("Excel file downloaded successfully");
    } catch (error) {
      notify.error("Failed to download Excel file");
    } finally {
      setDownloading(false);
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page, preserving state
    dispatch(clearCustomerDetailStateAction());
  };

  const handleViewCustomerDetail = (customerCode, customerName) => {
    // Encode customer code for URL
    const encodedCustomerCode = encode(customerCode);
    const encodedCustomerName = encode(customerName);
    const encodedApsCode = encode(apsCode);
    const encodedMonth = encode(month);

    navigate(`/planvsactual/customer-detail/${encodedCustomerCode}/${encodedCustomerName}/${encodedApsCode}/${encodedMonth}`);
  };

  const formatBackendValue = (value, options) => {
    if (value === null || value === undefined) return "-----";

    // If backend already sends string like "1.00 Lakh"
    if (typeof value === "string") return value;

    // If number → format
    return Number(value).toLocaleString("en-IN", options);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return "-----";

    if (typeof value === "string") return value;

    return `${Number(value).toFixed(2)}%`;
  };

  return (
    <div className="customer-detail-page-wrapper">
      <Header title="Plan vs Actual" />

      <div className="customer-detail-page-content">
        {/* Header Section */}
        <div className="customer-detail-page-header">
          <div className="header-info">
            <h2>Customer Details - {month}</h2>
            <p className="subtitle">
              <strong>Note: </strong> Below is the consolidated data of all the retailers under the selected APS
            </p>
            <p className="subtitle">
              Plan and Actual sales are calculated at MRP value and displayed in lakhs.
            </p>
          </div>
          <button className="close-btn" onClick={handleBack}>
            Close
          </button>
        </div>

        {/* Search Section */}
        <section className="search-section">
          <div className="search-controls">
            <select
              className="search-dropdown"
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value);
                setSearchValue("");
              }}
            >
              <option value="">Select Field</option>
              {searchFieldOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <div className="input-wrapper">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder={searchField ? `Enter ${searchFieldOptions.find(opt => opt.value === searchField)?.label}` : "Select a field first"}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={!searchField}
              />
              {(searchField || searchValue) && (
                <button className="clear-icon" onClick={handleClearSearch} disabled={loading}>
                  <FiX />
                </button>
              )}
            </div>

            <button
              className="search-btn"
              onClick={handleSearch}
              disabled={!searchField || !searchValue.trim() || loading}
            >
              Search
            </button>
          </div>
          {dataList?.length > 0 && (
            <button className="download-btn" onClick={handleDownloadExcel} disabled={downloading || loading}>
              <img src={Image_Url.EXCEL_LOGO} className="excel-icon" alt="Excel" />
              {downloading ? 'Downloading...' : 'Download Excel'}
            </button>
          )}
        </section>

        {/* Table Section */}
        <section className="table-section">
          {loading ? (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Customer Code</th>
                    <th>Customer Name</th>
                    <th>Month</th>
                    <th>Monthly Plan</th>
                    <th>Monthly Actual</th>
                    <th>Monthly Achievement %</th>
                    <th>YTD Plan</th>
                    <th>YTD Actual</th>
                    <th>YTD Achievement %</th>
                    {/* <th>Action</th> */}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="skeleton-row">
                      <td><span className="skeleton text w-10" /></td>
                      <td><span className="skeleton text w-20" /></td>
                      <td><span className="skeleton text w-32" /></td>
                      <td><span className="skeleton text w-20" /></td>
                      <td><span className="skeleton text w-24" /></td>
                      <td><span className="skeleton text w-24" /></td>
                      <td><span className="skeleton text w-20" /></td>
                      <td><span className="skeleton text w-24" /></td>
                      <td><span className="skeleton text w-24" /></td>
                      <td><span className="skeleton text w-20" /></td>
                      {/* <td><span className="skeleton text w-16" /></td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : dataList.length === 0 ? (
            <div className="no-data-wrapper">
              <NoDataFound />
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Customer Code</th>
                      <th>Customer Name</th>
                      <th>Month</th>
                      <th>Monthly Plan</th>
                      <th>Monthly Actual</th>
                      <th>Monthly Achievement %</th>
                      <th>YTD Plan</th>
                      <th>YTD Actual</th>
                      <th>YTD Achievement %</th>
                      {/* <th>Action</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {dataList.map((row, index) => (
                      <tr 
                        key={row.id || index}
                        className={isCurrentMonth(row.month) ? "current-month-row" : ""}
                      >
                        <td className="sno-cell">
                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </td>

                        <td className="customer-code-cell">{row.customer_code}</td>
                        <td className="customer-name-cell">{row.customer_name}</td>
                        <td className="month-cell">{formatMonth(month)}</td>

                        <td className="plan-cell">
                          {formatBackendValue(row.monthly_plan, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>

                        <td className="actual-cell">
                          {formatBackendValue(row.monthly_actual)}
                        </td>

                        <td className="achievement-cell">
                          {formatPercentage(row.monthly_pct)}
                        </td>

                        <td className="ytd-plan-cell">
                          {formatBackendValue(row.ytd_plan, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>

                        <td className="ytd-actual-cell">
                          {formatBackendValue(row.ytd_actual)}
                        </td>

                        <td className="ytd-achievement-cell">
                          {formatPercentage(row.ytd_pct)}
                        </td>
                      </tr>

                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="table-footer">
                <div className="results-info">
                  {totalCount > 0 ? `Showing ${((currentPage - 1) * ITEMS_PER_PAGE) + 1} to ${Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of ${totalCount} results` : 'No results'}
                </div>
                <div className="pagination">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="prev-btn"
                  >
                    Previous
                  </button>
                  {pageWindow.map((p, idx) =>
                    typeof p === "string" ? (
                      <button key={p + idx} className="page-btn ellipsis-btn" disabled>
                        …
                      </button>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`page-btn ${currentPage === p ? 'active' : ''}`}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="next-btn"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
